import { Nullable } from '../../../common/Nullable'

type AudioPlayerState = 'stopped' | 'playing'

export class AudioPlayer {
  private readonly audioContext: AudioContext
  private audioBuffer: Nullable<AudioBuffer> = null
  private audioSource: Nullable<AudioBufferSourceNode> = null
  private state: AudioPlayerState = 'stopped'

  constructor() {
    this.audioContext = new AudioContext()
  }

  public get initialized(): boolean {
    return this.audioBuffer != null
  }

  public get playing(): boolean {
    return this.state === 'playing'
  }

  public async init(source: Blob | string): Promise<void> {
    const arrayBuffer = typeof source === 'string' ? await (await fetch(source)).arrayBuffer() : await source.arrayBuffer()
    this.audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer)
  }

  private createAudioSource(): AudioBufferSourceNode {
    if (!this.audioBuffer) {
      throw new Error('AudioBuffer is not initialized.')
    }
    const audioSource = this.audioContext.createBufferSource()
    audioSource.buffer = this.audioBuffer
    audioSource.connect(this.audioContext.destination)
    return audioSource
  }

  public async play({ playingFinishedCallback }: { playingFinishedCallback: () => void }): Promise<void> {
    if (!this.audioBuffer) {
      throw new Error('Audio buffer is not loaded.')
    }
    if (this.state === 'playing') {
      return
    }
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume()
    }
    const audioSource = this.createAudioSource()
    this.audioSource = audioSource
    return new Promise<void>((resolve) => {
      audioSource.onended = () => {
        audioSource.disconnect()
        this.audioSource = null
        this.state = 'stopped'
        playingFinishedCallback()
        resolve()
      }
      audioSource.start()
      this.state = 'playing'
    })
  }

  public stop(): void {
    this.audioSource?.stop()
  }

  public async close(): Promise<void> {
    this.stop()
    if (this.audioContext.state !== 'closed') {
      await this.audioContext.close()
    }
  }
}
